import { Component, Input, Output, EventEmitter } from '@angular/core';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { AgentConfigService } from 'src/app/agents/services/agent-config/agent-config.service';
import { VisualizationData } from '../../models/response.models';
import type { AgentFilePreviewRequest } from '../agent-file-attachment-card/agent-file-attachment-card.component';

/**
 * Shared Response Renderer Component
 * 
 * Renders chat responses with support for:
 * - Markdown formatted text
 * - Image visualizations
 * - Chart visualizations (Plotly)
 * - Loading states
 * - Error messages
 * 
 * Used by agent and chatbot interfaces to display responses
 */
@Component({
  selector: 'app-shared-response-renderer',
  templateUrl: './response-renderer.component.html',
  styleUrls: ['./response-renderer.component.scss']
})
export class SharedResponseRendererComponent {
  /**
   * Main response text content
   */
  @Input() answer: string = '';
  
  /**
   * Base64 or URL image visualization
   */
  @Input() imageVisualization: string = '';
  
  /**
   * Chart visualization data for Plotly
   */
  @Input() visualizationData: VisualizationData[] = [];
  
  /**
   * Indicates if content is loading
   */
  @Input() isLoading: boolean = false;
  
  /**
   * Indicates if content is streaming
   */
  @Input() isStreaming: boolean = false;
  
  /**
   * Error message to display
   */
  @Input() errorMessage: string = '';

  /**
   * Current agent ID (used for agent-specific markdown behaviors).
   */
  @Input() agentId: string = '';

  /**
   * Fusion App UI base URL used for regulation hyperlink generation.
   */
  @Input() fusionAppUiUrl: string = '';

  /**
   * When false, INS ids in markdown are plain text (shared chat has no regulation redirect).
   */
  @Input() enableInsLinkNavigation: boolean = true;
  
  /**
   * Show or hide loading indicator
   */
  @Input() showLoadingIndicator: boolean = true;
  
  /**
   * Enable auto-scroll during streaming
   */
  @Input() enableAutoScroll: boolean = true;

  /**
   * When false, RIA file attachment cards render display-only (no preview/download).
   * Used by the shared chat viewer, which has no preview modal / download wiring.
   */
  @Input() showFileAttachmentActions: boolean = true;

  /**
   * When false, rich tables render as read-only (no search, column filters, or download).
   * Used by the shared chat viewer for static table display.
   */
  @Input() enableTableInteractions: boolean = true;

  /** S3 paths from history metadata for attachments not embedded in answer text. */
  @Input() filePathsInOrder: string[] = [];

  @Output() tableDownloaded = new EventEmitter<string>();
  @Output() tableDownloadFailed = new EventEmitter<void>();
  @Output() filePreviewRequested = new EventEmitter<AgentFilePreviewRequest>();
  @Output() filePreviewFailed = new EventEmitter<string>();
  @Output() fileDownloadCompleted = new EventEmitter<string>();
  @Output() fileDownloadFailed = new EventEmitter<string>();

  get enableRichTables(): boolean {
    return this.agentId.trim().length > 0;
  }

  get enableS3FileAttachments(): boolean {
    return this.agentId === 'ria_agent';
  }

  get enableTableSearch(): boolean {
    if (!this.enableTableInteractions) {
      return false;
    }
    return this.agentConfigService.getAgentConfig(this.agentId)?.options?.tableSearch === true;
  }

  get enableTableColumnFilter(): boolean {
    if (!this.enableTableInteractions) {
      return false;
    }
    return this.agentConfigService.getAgentConfig(this.agentId)?.options?.tableColumnFilter === true;
  }

  get enableTableDownload(): boolean {
    if (!this.enableTableInteractions) {
      return false;
    }
    if (this.agentId === 'regulations_agent') {
      return this.agentConfigService.getAgentConfig(this.agentId)?.options?.tableDownload === true;
    }
    return true;
  }

  get isTablePaginationEnabled(): boolean {
    return this.agentConfigService.getAgentConfig(this.agentId)?.options?.isTablePaginationEnabled !== false;
  }

  /** RIA only: blinking cursor after response text has started streaming. */
  get showStreamingCursor(): boolean {
    return this.isStreaming
      && this.agentId === 'ria_agent'
      && !!this.answer?.trim();
  }

  constructor(
    private readonly logger: LoggerService,
    private readonly agentConfigService: AgentConfigService,
  ) {}

  /**
   * Checks if there is any content to display
   * @returns true if answer, image, or visualization data exists
   */
  hasContent(): boolean {
    try {
      return !!(this.answer || this.imageVisualization || this.visualizationData?.length > 0);
    } catch (error: any) {
      this.logger.logInfo(`Error in hasContent: ${error?.message || error}`);
      return false;
    }
  }

  /**
   * Checks if visualization data is available
   * @returns true if visualization data exists
   */
  hasVisualization(): boolean {
    try {
      return !!(this.visualizationData && this.visualizationData.length > 0);
    } catch (error: any) {
      this.logger.logInfo(`Error in hasVisualization: ${error?.message || error}`);
      return false;
    }
  }
}

